import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

interface ThemeColors {
  primary: string;
  secondary: string;
  tertiary: string;
  background: string;
  surface: string;
  surfaceHover: string;
  surfaceSecondary: string;
  text: {
    primary: string;
    secondary: string;
    tertiary: string;
    inverse: string;
  };
  border: {
    light: string;
    medium: string;
    dark: string;
  };
  status: {
    success: string;
    warning: string;
    danger: string;
    info: string;
  };
  severity: {
    critical: string;
    high: string;
    medium: string;
    low: string;
    info: string;
  };
}

interface ThemeGradients {
  primary: string;
  secondary: string;
  success: string;
  danger: string;
  warning: string;
  info: string;
  heading: string;
  surface: string;
}

interface Theme {
  colors: ThemeColors;
  gradients: ThemeGradients;
  isDarkMode: boolean;
  fonts: {
    primary: string;
    mono: string;
    heading: string;
  };
  breakpoints: {
    mobile: string;
    tablet: string;
    desktop: string;
    wide: string;
  };
  shadows: {
    small: string;
    medium: string;
    large: string;
    xl: string;
  };
  spacing: {
    xs: string;
    sm: string;
    md: string;
    lg: string;
    xl: string;
    xxl: string;
  };
  borderRadius: {
    small: string;
    medium: string;
    large: string;
    xl: string;
  };
}

interface ThemeContextValue {
  theme: Theme;
  isDarkMode: boolean;
  toggleDarkMode: () => void;
}

interface ThemeProviderProps {
  children: React.ReactNode;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const lightTheme: { colors: ThemeColors; gradients: ThemeGradients } = {
  colors: {
    // Base colors
    primary: '#1336CC',
    secondary: '#FCC116',
    tertiary: '#0070F3',

    // Background colors
    background: '#FFFFFF',
    surface: '#FFFFFF',
    surfaceHover: '#F8FAFC',
    surfaceSecondary: '#F1F5F9',

    // Text colors
    text: {
      primary: '#1A202C',
      secondary: '#64748B',
      tertiary: '#94A3B8',
      inverse: '#FFFFFF',
    },

    // Border colors
    border: {
      light: '#E2E8F0',
      medium: '#CBD5E1',
      dark: '#94A3B8',
    },

    // Status colors
    status: {
      success: '#10B981',
      warning: '#F59E0B',
      danger: '#EF4444',
      info: '#3B82F6',
    },

    // Severity colors
    severity: {
      critical: '#DC2626',
      high: '#EF4444',
      medium: '#F59E0B',
      low: '#10B981',
      info: '#3B82F6',
    },
  },
  gradients: {
    primary: 'linear-gradient(135deg, #1336CC 0%, #0070F3 100%)',
    secondary: 'linear-gradient(135deg, #FCC116 0%, #FFD700 100%)',
    success: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
    danger: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
    warning: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
    info: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)',
    heading: 'linear-gradient(135deg, #1336CC 0%, #8B5CF6 100%)',
    surface: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
  },
};

const darkTheme: { colors: ThemeColors; gradients: ThemeGradients } = {
  colors: {
    // Base colors
    primary: '#60A5FA',
    secondary: '#FDE047',
    tertiary: '#38BDF8',

    // Background colors
    background: '#0F172A',
    surface: '#1E293B',
    surfaceHover: '#334155',
    surfaceSecondary: '#475569',

    // Text colors
    text: {
      primary: '#F8FAFC',
      secondary: '#CBD5E1',
      tertiary: '#94A3B8',
      inverse: '#1A202C',
    },

    // Border colors
    border: {
      light: '#334155',
      medium: '#475569',
      dark: '#64748B',
    },

    // Status colors
    status: {
      success: '#22C55E',
      warning: '#EAB308',
      danger: '#F87171',
      info: '#60A5FA',
    },

    // Severity colors
    severity: {
      critical: '#F87171',
      high: '#FB7185',
      medium: '#FBBF24',
      low: '#34D399',
      info: '#60A5FA',
    },
  },
  gradients: {
    primary: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
    secondary: 'linear-gradient(135deg, #FDE047 0%, #FACC15 100%)',
    success: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
    danger: 'linear-gradient(135deg, #F87171 0%, #EF4444 100%)',
    warning: 'linear-gradient(135deg, #FBBF24 0%, #EAB308 100%)',
    info: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
    heading: 'linear-gradient(135deg, #60A5FA 0%, #A855F7 100%)',
    surface: 'linear-gradient(135deg, #1E293B 0%, #334155 100%)',
  },
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  // Emit CSS custom properties for CSS Modules
  useEffect(() => {
    const t = isDarkMode ? darkTheme : lightTheme;
    const root = document.documentElement;

    // Colors — flat
    root.style.setProperty('--color-primary', t.colors.primary);
    root.style.setProperty('--color-secondary', t.colors.secondary);
    root.style.setProperty('--color-tertiary', t.colors.tertiary);
    root.style.setProperty('--color-background', t.colors.background);
    root.style.setProperty('--color-surface', t.colors.surface);
    root.style.setProperty('--color-surface-hover', t.colors.surfaceHover);
    root.style.setProperty('--color-surface-secondary', t.colors.surfaceSecondary);

    // Colors — text
    root.style.setProperty('--color-text-primary', t.colors.text.primary);
    root.style.setProperty('--color-text-secondary', t.colors.text.secondary);
    root.style.setProperty('--color-text-tertiary', t.colors.text.tertiary);
    root.style.setProperty('--color-text-inverse', t.colors.text.inverse);

    // Colors — border
    root.style.setProperty('--color-border-light', t.colors.border.light);
    root.style.setProperty('--color-border-medium', t.colors.border.medium);
    root.style.setProperty('--color-border-dark', t.colors.border.dark);

    // Colors — status
    root.style.setProperty('--color-status-success', t.colors.status.success);
    root.style.setProperty('--color-status-warning', t.colors.status.warning);
    root.style.setProperty('--color-status-danger', t.colors.status.danger);
    root.style.setProperty('--color-status-info', t.colors.status.info);

    // Colors — severity
    root.style.setProperty('--color-severity-critical', t.colors.severity.critical);
    root.style.setProperty('--color-severity-high', t.colors.severity.high);
    root.style.setProperty('--color-severity-medium', t.colors.severity.medium);
    root.style.setProperty('--color-severity-low', t.colors.severity.low);
    root.style.setProperty('--color-severity-info', t.colors.severity.info);

    // Gradients
    root.style.setProperty('--gradient-primary', t.gradients.primary);
    root.style.setProperty('--gradient-secondary', t.gradients.secondary);
    root.style.setProperty('--gradient-success', t.gradients.success);
    root.style.setProperty('--gradient-danger', t.gradients.danger);
    root.style.setProperty('--gradient-warning', t.gradients.warning);
    root.style.setProperty('--gradient-info', t.gradients.info);
    root.style.setProperty('--gradient-heading', t.gradients.heading);
    root.style.setProperty('--gradient-surface', t.gradients.surface);

    // Shadows (theme-dependent)
    const shadows = isDarkMode
      ? {
          small: '0 1px 3px 0 rgba(0, 0, 0, 0.3), 0 1px 2px 0 rgba(0, 0, 0, 0.2)',
          medium: '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)',
          large: '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)',
          xl: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)',
        }
      : {
          small: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          medium: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          large: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        };
    root.style.setProperty('--shadow-small', shadows.small);
    root.style.setProperty('--shadow-medium', shadows.medium);
    root.style.setProperty('--shadow-large', shadows.large);
    root.style.setProperty('--shadow-xl', shadows.xl);

    // Fonts (static)
    root.style.setProperty(
      '--font-primary',
      '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    );
    root.style.setProperty('--font-mono', '"JetBrains Mono", "Fira Code", Monaco, Consolas, monospace');
    root.style.setProperty('--font-heading', '"Inter", -apple-system, BlinkMacSystemFont, sans-serif');

    // Spacing (static)
    root.style.setProperty('--spacing-xs', '0.5rem');
    root.style.setProperty('--spacing-sm', '1rem');
    root.style.setProperty('--spacing-md', '1.5rem');
    root.style.setProperty('--spacing-lg', '2rem');
    root.style.setProperty('--spacing-xl', '3rem');
    root.style.setProperty('--spacing-xxl', '4rem');

    // Border radius (static)
    root.style.setProperty('--border-radius-small', '0px');
    root.style.setProperty('--border-radius-medium', '0px');
    root.style.setProperty('--border-radius-large', '0px');
    root.style.setProperty('--border-radius-xl', '0px');
  }, [isDarkMode]);

  const toggleDarkMode = useCallback((): void => {
    setIsDarkMode((prev) => !prev);
  }, []);

  const theme = useMemo<Theme>(
    () => ({
      ...(isDarkMode ? darkTheme : lightTheme),
      isDarkMode,
      fonts: {
        primary: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        mono: '"JetBrains Mono", "Fira Code", Monaco, Consolas, monospace',
        heading: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      },
      breakpoints: {
        mobile: '768px',
        tablet: '1024px',
        desktop: '1200px',
        wide: '1400px',
      },
      shadows: {
        small: isDarkMode
          ? '0 1px 3px 0 rgba(0, 0, 0, 0.3), 0 1px 2px 0 rgba(0, 0, 0, 0.2)'
          : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        medium: isDarkMode
          ? '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)'
          : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        large: isDarkMode
          ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
          : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        xl: isDarkMode
          ? '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)'
          : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      },
      spacing: {
        xs: '0.5rem',
        sm: '1rem',
        md: '1.5rem',
        lg: '2rem',
        xl: '3rem',
        xxl: '4rem',
      },
      borderRadius: {
        small: '0px',
        medium: '0px',
        large: '0px',
        xl: '0px',
      },
    }),
    [isDarkMode],
  );

  const value = useMemo(() => ({ theme, isDarkMode, toggleDarkMode }), [theme, isDarkMode, toggleDarkMode]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
